/**
 * fetchWithAuth — the canonical entrypoint for authenticated HTTP from
 * any Coach Cedar service.
 *
 * Why this exists
 * ===============
 * Before Wave 1.5, every service did this by hand:
 *
 *     const token = await getAuthToken();
 *     const res = await fetch(url, { headers: { Authorization: `Bearer ${token}`, ... } });
 *     // …if 401, ¯\_(ツ)_/¯
 *
 * That pattern was duplicated across 250+ call sites. None of them
 * handled token rotation. Long-running operations (HealthKit sync,
 * plan synthesis) crossed Auth0's expiry window and 401-ed silently.
 *
 * fetchWithAuth wraps the pattern correctly, in one place:
 *
 *   1. Ask TokenManager for a valid token (proactive refresh if expiring).
 *   2. Issue the request with the Authorization header attached.
 *   3. If the server returns 401, force a refresh and retry exactly once.
 *   4. Return whatever the final attempt yielded — the caller decides
 *      what to do with a 4xx/5xx response.
 *
 * Why exactly one retry
 * ---------------------
 * - 401 + refresh-fail → return the 401. User likely needs to re-login;
 *   the AuthProvider's listener on TokenManager will surface that.
 * - 401 → refresh succeeds → retry → 401 again → return the second 401.
 *   The token IS fresh (Auth0 confirmed) but the backend still rejects;
 *   user was likely deleted server-side or some other terminal state.
 *   Retrying further would loop indefinitely.
 *
 * What this is NOT
 * ----------------
 * - Not a generic HTTP client. No retry-on-5xx, no exponential backoff,
 *   no request body encoding. Drop in next to fetch and migrate
 *   mechanically.
 * - Not auth-aware enough to handle 403. 403 means "authenticated but
 *   not authorized" — refreshing won't help. Passes through unchanged.
 * - Not a logout handler. When refresh returns null repeatedly, the
 *   AuthProvider listener decides whether to force a logout. This
 *   wrapper just surfaces the 401.
 *
 * Migration shape
 * ---------------
 * Before:
 *     const token = await getAuthToken();
 *     const res = await fetch(`${API_BASE_URL}/api/foo`, {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ... },
 *       body: JSON.stringify(payload),
 *     });
 * After:
 *     const res = await fetchWithAuth(`${API_BASE_URL}/api/foo`, {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json', ... },
 *       body: JSON.stringify(payload),
 *     });
 *
 * The Authorization header is auto-attached. Other headers (Content-Type,
 * X-Cedar-Platform, etc.) are preserved.
 *
 * See web/src/services/__tests__/fetchWithAuth.test.ts for the contract.
 */

import { NO_CREDENTIAL_HEADER, isNoCredentialResponse } from './authResponse';
import { getTokenManager } from '../auth/tokenManager';
import { noteTokenIssued } from '../auth/staleCredentialDetector';
import {
  attributeAbort,
  createTimeoutScope,
  resolveTimeoutForUrl,
} from './requestTimeout';

/**
 * Merge the caller's headers with an Authorization header, preserving
 * the caller's original shape (plain object vs Headers instance).
 *
 * Different callers in the codebase use different shapes — most use
 * plain objects, but a few use `new Headers()`. This helper preserves
 * either so we don't accidentally drop case-sensitive header keys
 * during migration.
 */
function withAuthHeader(
  callerHeaders: HeadersInit | undefined,
  token: string,
): HeadersInit {
  if (callerHeaders instanceof Headers) {
    // Return a new Headers; mutating callerHeaders would surprise the
    // caller if they re-use the object.
    const merged = new Headers(callerHeaders);
    merged.set('Authorization', `Bearer ${token}`);
    return merged;
  }
  // Plain object or undefined — coerce to a fresh object.
  return {
    ...((callerHeaders as Record<string, string>) ?? {}),
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Header stamped on the synthetic 401 this module returns when there is no
 * credential to send.
 *
 * WHY A MARKER IS NECESSARY
 * =========================
 * "We never asked" and "the server said no" are completely different facts, and
 * before this they were both bare `status: 401`. That ambiguity is the shape
 * behind the 2026-08 data loss: a client with no token fires its whole boot
 * fan-out, every call comes back 401, and code that means "your session is
 * dead" runs against a request that was never made.
 *
 * Production evidence (3-day sweep, 2026-08-19): `reason=missing_token` 242
 * events vs `reason=expired` 117 — the dominant server-side rejection is a
 * client sending NO Authorization header at all. One client fired seven
 * requests in 240ms (hr-zones, goals, plans, workouts, mobility/routines,
 * rest-days, config/revenuecat-key), every one missing_token, and made no
 * successful request for ten minutes.
 *
 * Migrating those call sites onto `fetchWithAuth` removes the SERVER events —
 * it does not, on its own, stop the caller treating the result as a rejection.
 * The marker is what makes the distinction available at the call site.
 */
/**
 * Build a synthetic 401 Response for the no-token case. We never fire
 * a network request when we have no token — surface a uniform 401 so
 * the caller's existing 4xx handling kicks in.
 *
 * The status stays 401 so every pre-existing caller behaves exactly as it did;
 * the marker header is purely additive, for callers that need the distinction.
 */
function syntheticUnauthorized(): Response {
  return new Response(
    JSON.stringify({ detail: 'No auth token available' }),
    {
      status: 401,
      statusText: 'Unauthorized',
      headers: {
        'Content-Type': 'application/json',
        [NO_CREDENTIAL_HEADER]: '1',
      },
    },
  );
}

/**
 * Authenticated fetch with proactive expiry refresh, reactive 401
 * refresh-and-retry, and exactly-one retry semantics.
 *
 * The signature mirrors the standard `fetch(url, init?)` so migration
 * from raw `fetch` is mechanical: replace the function name and remove
 * the manual Authorization header.
 *
 * TIMEOUTS (2026-08-31 — mattmayer, 8h43m of total app unavailability)
 * ------------------------------------------------------------------
 * Every attempt is bounded. Before this, a half-dead network path produced
 * a promise that never settled: no error, no retry, no telemetry, and
 * nothing on the wire for us to observe. See `./requestTimeout` for the
 * incident and for the four RN runtime constraints the implementation obeys.
 *
 * The budget is PER ATTEMPT, not per call. A 401-refresh-retry legitimately
 * doubles wall time, and charging both attempts to one budget would abort
 * healthy requests.
 *
 * ⚠️ SCOPE OF THE BOUND, by platform. On native (whatwg-fetch over XHR) the
 * promise settles only after the full body arrives, so the budget covers the
 * whole response. On web (react-native-web -> real fetch) it resolves at
 * HEADERS, so a body that stalls mid-stream is NOT bounded here — the
 * caller's own `res.json()` is unguarded. Native is the platform this fix
 * targets; the web gap is known and deliberately out of scope.
 *
 * A caller's own `init.signal` still works and still cancels — it is
 * COMPOSED with the timeout, never replaced. `offlineCompletionQueue:321`
 * depends on this: it passes its own 30s controller through this function,
 * and clobbering it would silently remove the bound that stops one hung
 * socket pinning its whole drain open.
 *
 * @param url   Target URL. Same shape as fetch.
 * @param init  Standard RequestInit. Authorization header is added by
 *              this function — callers should not set it. `signal` is
 *              honoured and composed with the timeout.
 * @param opts  Optional per-call overrides. `timeoutMs: 0` disables the
 *              timeout entirely — required for any streaming caller, where
 *              a body that arrives over minutes is healthy, not hung.
 * @returns     The final Response (after at most one retry on 401).
 *              A timeout throws an Error named 'AbortError' carrying
 *              `isTimeout: true` — see `isTimeoutError`. Other network
 *              errors still throw, matching `fetch` semantics.
 */
export async function fetchWithAuth(
  url: string,
  init: RequestInit = {},
  opts: { timeoutMs?: number } = {},
): Promise<Response> {
  const tm = getTokenManager();
  // Background health-sync legitimately runs minutes; interactive traffic
  // does not. One budget cannot serve both — see `resolveTimeoutForUrl`.
  const timeoutMs = opts.timeoutMs ?? resolveTimeoutForUrl(url);

  // Honour a caller that has ALREADY given up before we do any work.
  // Without this, an abort raised while we are inside `getValidToken()`
  // below has no effect at all: that phase takes no signal, so a screen
  // that unmounted still waits out the whole token path before its
  // cancellation is noticed.
  if (init.signal?.aborted) {
    const err = new Error('Request aborted before it was issued');
    err.name = 'AbortError';
    throw err;
  }

  /**
   * Issue one bounded attempt.
   *
   * The scope is created immediately before the fetch and disposed in a
   * `finally` that runs on every path including throw — an abort timer
   * that outlives its request fires later against a controller nobody is
   * listening to, and across the two attempts that leaks one live timer
   * per call.
   */
  const issue = async (bearer: string): Promise<Response> => {
    const scope = createTimeoutScope(init.signal, timeoutMs, url);
    try {
      return await fetch(url, {
        ...init,
        headers: withAuthHeader(init.headers, bearer),
        // Only override `signal` when we actually created a scope. With
        // the timeout disabled the caller's own signal must pass through
        // untouched via the spread above.
        ...(scope ? { signal: scope.signal } : {}),
      });
    } catch (err) {
      // `fetch` rejects with its own AbortError object, which cannot say
      // WHY it aborted. Re-attribute so the caller can tell "we ran out of
      // time" (retry it) from "the user cancelled" (do not).
      const attributed = attributeAbort(err, scope, timeoutMs, url);

      if (attributed !== err) {
        // It was OUR budget, not a caller cancel. This is the only
        // observation point for a failure class that leaves no
        // server-side trace — see reportRequestTimeout for what it can
        // and cannot see. Required lazily and fully guarded: telemetry
        // must never be able to change the outcome of a request.
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          require('./clientErrorReporter').reportRequestTimeout(url, timeoutMs);
        } catch {
          /* telemetry is best-effort */
        }
      }

      throw attributed;
    } finally {
      scope?.dispose();
    }
  };

  // 1. Get a valid token (proactive refresh kicks in if the JWT is
  //    within the warning window — see TokenManager.getValidToken).
  //
  //    NOTE: this await precedes every fetch below, so bounding the
  //    fetches alone does NOT bound this function. TokenManager owns its
  //    own timeouts; without them a hung refresh wedges every caller here
  //    before a single request is issued. That is the actual mechanism
  //    behind the 8h43m lockout.
  const token = await tm.getValidToken();
  if (!token) {
    // No token at all — user is logged out, or pre-login. Return a
    // synthetic 401 so callers' existing 4xx handling fires.
    return syntheticUnauthorized();
  }

  // 2. First attempt. Network errors propagate to the caller's existing
  //    try/catch — this is consistent with raw fetch's behaviour.
  //
  //    Before issuing, note the credential. This path already asked
  //    TokenManager for the freshest token, so a STALE_CREDENTIAL_USED
  //    firing here is the high-value one: it means a persist failure is
  //    being swallowed and the canonical path is serving a spent token.
  noteTokenIssued(token, 'fetchWithAuth');
  const response = await issue(token);

  if (response.status !== 401) {
    return response;
  }

  // 3. Reactive refresh. The server explicitly rejected our token —
  //    force a refresh (concurrency-deduped inside TokenManager) and
  //    retry exactly once with the new token.
  const refreshed = await tm.refresh();
  if (!refreshed) {
    // Refresh failed (network, invalid_grant, backend reject). Return
    // the original 401 so the AuthProvider's listener can decide
    // whether to log the user out.
    return response;
  }

  // 4. Exactly one retry. If THIS attempt also 401s, the user's session
  //    is in a terminal state (e.g. deleted server-side); we don't loop.
  //    The body is the same string the caller passed — no stream-like
  //    body to be exhausted between attempts. (If a future caller
  //    passes a ReadableStream, this contract changes; for now every
  //    Cedar caller passes JSON.stringify output.)
  return await issue(refreshed);
}

/**
 * Re-exported for callers that already import from this module. The definitions live
 * in `./authResponse` because that leaf has zero imports and is safe in any test.
 */
export { NO_CREDENTIAL_HEADER, isNoCredentialResponse };
